import { X } from 'lucide-react';
import type { CampaignFilters, CampaignStatus } from '../types/campaign';
import { useLanguage } from '../contexts/LanguageContext';

interface FilterChip {
  key: string;
  label: string;
  value: string;
  onRemove: () => void;
}

interface FilterChipsProps {
  selectedStatuses: CampaignStatus[];
  onStatusRemove: (status: CampaignStatus) => void;
  onClearStatuses: () => void;
  filters: CampaignFilters;
  onFilterRemove: (key: keyof CampaignFilters) => void;
  onClearAll: () => void;
}

export function FilterChips({
  selectedStatuses,
  onStatusRemove,
  onClearStatuses,
  filters,
  onFilterRemove,
  onClearAll,
}: FilterChipsProps) {
  const { t, ts } = useLanguage();

  const getStatusLabel = (status: CampaignStatus): string => {
    const labels: Record<CampaignStatus, string> = {
      'Consultation': ts('Consultation'),
      'Confirmed': ts('Confirmed'),
      'In Progress': ts('In Progress'),
      'Billing': ts('Billing'),
      'Completed': ts('Completed'),
    };
    return labels[status] || status;
  };

  const getFilterLabel = (key: keyof CampaignFilters): string => {
    const labels: Record<string, string> = {
      year: t('filter.year'),
      brand: t('filter.brand'),
      influencer: t('filter.influencer'),
      agent_name: t('filter.agent'),
      campaign_status: t('filter.status'),
      company_invoice_status: t('filter.companyInvoice'),
      client_payment_status: t('filter.paymentStatus'),
      invoice_number: t('filter.invoiceNumber'),
    };
    return labels[key] || key;
  };

  const getFilterValue = (key: keyof CampaignFilters, value: unknown): string => {
    if (key === 'company_invoice_status' || key === 'client_payment_status' || key === 'campaign_status') {
      return ts(String(value));
    }
    return String(value);
  };

  const filterChips: FilterChip[] = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (key !== 'search' && value !== undefined) {
      filterChips.push({
        key,
        label: getFilterLabel(key as keyof CampaignFilters),
        value: getFilterValue(key as keyof CampaignFilters, value),
        onRemove: () => onFilterRemove(key as keyof CampaignFilters),
      });
    }
  });

  const hasActiveFilters = selectedStatuses.length > 0 || filterChips.length > 0;

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {selectedStatuses.map((status) => (
        <span
          key={`status-${status}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-full"
        >
          <span className="text-gray-400 text-xs">{t('filter.status')}:</span>
          {getStatusLabel(status)}
          <button
            onClick={() => onStatusRemove(status)}
            className="ml-0.5 p-0.5 hover:bg-gray-700 rounded-full transition-colors"
            aria-label={`Remove ${status} filter`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      ))}

      {filterChips.map((chip) => {
        const isYearFilter = chip.key === 'year';
        return (
          <span
            key={chip.key}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full ${
              isYearFilter
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            <span className={isYearFilter ? 'text-gray-400 text-xs' : 'text-gray-500 text-xs'}>{chip.label}:</span>
            {chip.value}
            <button
              onClick={chip.onRemove}
              className={`ml-0.5 p-0.5 rounded-full transition-colors ${
                isYearFilter ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
              }`}
              aria-label={`Remove ${chip.label} filter`}
            >
              <X className={`w-3.5 h-3.5 ${isYearFilter ? '' : 'text-gray-500'}`} />
            </button>
          </span>
        );
      })}

      {hasActiveFilters && (
        <button
          onClick={() => {
            onClearStatuses();
            onClearAll();
          }}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors ml-2"
        >
          {t('filter.clearAll')}
        </button>
      )}
    </div>
  );
}
